import React, { useEffect, useState } from 'react';

import i18n from 'i18n-js';
import * as Yup from 'yup';
import { capitalize } from 'utils';
import { cloneDeep } from 'lodash';
import { Formik, Form, Field } from 'formik';
import { SelectableList } from 'components/UI';
import CheckIcon from '@material-ui/icons/Check';
import { TextField } from 'components/FormFields';
import useStyles from './BrokersConnection.styled';
import { Box, Grid, Button, Typography } from '@material-ui/core';

const BrokersConnection = ({
  errors,
  loading,
  assigned,
  availables,
  onAddBroker,
  onRemoveBroker,
}) => {
  const classes = useStyles();

  const [assignedBrokers, setAssignedBrokers] = useState([]);
  const [availablesBrokers, setAvailablesBrokers] = useState([]);
  const [selectedAvailableRow, setSelectedAvailableRow] = useState(null);
  const [selectedAssignedRow, setSelectedAssignedRow] = useState(null);

  const formSchema = Yup.object().shape({
    ext_id: Yup.string().required(i18n.t('common.required_field')),
  });

  const availablesBrokersColumns = [
    {
      name: i18n.t('brokers.name'),
      sortable: false,
      selector: 'name',
      cell: (row) => (
        <Typography component="span">
          <Box fontSize={12}>{capitalize(row?.name?.toLowerCase() || '--')}</Box>
        </Typography>
      ),
    },
  ];

  const assignedBrokersColumns = [
    {
      name: i18n.t('brokers.name'),
      sortable: false,
      selector: 'name',
      cell: (row) => (
        <Typography component="span">
          <Box fontSize={12}>{capitalize(row?.name?.toLowerCase() || '--')}</Box>
        </Typography>
      ),
    },
    {
      name: i18n.t('brokers.status'),
      sortable: false,
      selector: 'status',
      cell: (row) => (
        <Box display="flex" alignItems="center">
          <CheckIcon className={classes.checkIcon} />
          <Typography component="span">
            <Box fontSize={12}>
              {capitalize(row?.status?.toLowerCase() || i18n.t('status.ACTIVE'))}
            </Box>
          </Typography>
        </Box>
      ),
    },
  ];

  const brokerAction = (type, payload, action) => {
    if (type === 'add') return onAddBroker(payload, action);

    return onRemoveBroker(selectedAssignedRow);
  };

  useEffect(() => {
    if (assigned) {
      setAssignedBrokers(parseAssignedBrokers(assigned) || []);
    }

    if (availables?.list) {
      setAvailablesBrokers([...filteredBrokers()] || []);
    }

    setSelectedAssignedRow(null);
    setSelectedAvailableRow(null);
  }, [availables, assigned]); // eslint-disable-line

  const parseAssignedBrokers = () => {
    const brokers = [];
    Object.keys(assigned).forEach((key) => {
      assigned[key].vendor_slug = key;
      brokers.push(assigned[key]);
    });
    return brokers;
  };

  const filteredBrokers = () => {
    const brokers = cloneDeep(availables?.list) || [];
    const assignedBrokers = [...(parseAssignedBrokers(assigned) || [])];

    const filteredArray = brokers.map((broker) => {
      if (assignedBrokers?.length === 0) {
        broker.disabled = false;
        if (!loading) {
          broker.selected = false;
        }
        return broker;
      }

      let disabled = false;

      for (let i = 0; i < assignedBrokers.length; i++) {
        if (assignedBrokers[i]?.broker_slug === broker.broker_slug) {
          broker.disabled = true;
          disabled = true;
        } else if (!disabled) {
          broker.disabled = false;
        }
      }
      return broker;
    });

    return filteredArray;
  };

  const onAvailablesRowClicked = (row) => {
    setSelectedAvailableRow(row);
  };

  const onAssignedRowClicked = (row) => {
    setSelectedAssignedRow(row);
  };

  return (
    <Grid container direction="row" justifyContent="space-around" alignItems="flex-start">
      <Grid item xs={4} sm={4} md={4} lg={4}>
        <SelectableList
          mainTable={true}
          pagination={false}
          field="broker_slug"
          dataCy="available-list"
          loading={availables?.loading}
          data={availablesBrokers || []}
          columns={availablesBrokersColumns}
          onRowClicked={onAvailablesRowClicked}
          title={i18n.t('brokers.available_brokers')}
          disableSelectable={selectedAssignedRow ? true : false}
        />
      </Grid>
      <Grid item xs={4} sm={4} md={4} lg={4}>
        <Box display="flex" alignItems="center" flexDirection="column" justifyContent="center">
          <Box py={1} px={2} textAlign="center" style={{ width: '100%' }}>
            <Button
              onClick={() => {
                brokerAction('add', { broker: selectedAvailableRow?.broker_slug });
              }}
              disabled={loading || !selectedAvailableRow}
              variant="outlined"
              classes={{
                root: classes.btn,
              }}
              color="primary"
              fullWidth
              data-cy="connect-broker"
            >
              {i18n.t('brokers.connect_automatically')}
            </Button>
          </Box>
          <Typography align="left" className={classes.or}>
            <Box fontSize={10} fontWeight="bold" my={1}>
              {i18n.t('common.or')}
            </Box>
          </Typography>
          <Box py={1} px={2} textAlign="center" style={{ width: '100%' }}>
            <Formik
              initialValues={{
                ext_id: '',
              }}
              onSubmit={(values, props) => {
                const payload = {
                  ...values,
                  broker: selectedAvailableRow?.broker_slug,
                };
                brokerAction('add', payload, props.resetForm);
              }}
              validationSchema={formSchema}
            >
              {({ handleSubmit, values }) => (
                <Form noValidate onSubmit={handleSubmit} autoComplete="off">
                  <Field
                    type="text"
                    margin="none"
                    name="ext_id"
                    component={TextField}
                    asyncErrors={errors?.ext_id}
                    label={i18n.t('brokers.vehicle_id')}
                    style={{ maxWidth: 240, margin: '0 auto', marginBottom: 16 }}
                  />
                  <Button
                    fullWidth
                    type="submit"
                    variant="outlined"
                    classes={{
                      root: classes.btn,
                    }}
                    color="primary"
                    data-cy="connect-manually-broker"
                    disabled={loading || !selectedAvailableRow || !values.ext_id}
                  >
                    {i18n.t('brokers.connect_manually')}
                  </Button>
                </Form>
              )}
            </Formik>
          </Box>
          <Typography align="left" className={classes.or}>
            <Box fontSize={10} fontWeight="bold" my={1}>
              {i18n.t('common.or')}
            </Box>
          </Typography>
          <Box py={1} px={2} textAlign="center" style={{ width: '100%' }}>
            <Button
              onClick={() => {
                brokerAction();
              }}
              disabled={loading || !selectedAssignedRow}
              variant="outlined"
              classes={{
                root: classes.btn,
              }}
              color="primary"
              fullWidth
              data-cy="disconnect-broker"
            >
              {i18n.t('brokers.disconnect')}
            </Button>
          </Box>
        </Box>
      </Grid>
      <Grid item xs={4} sm={4} md={4} lg={4}>
        <Box mb={5}>
          <SelectableList
            pagination={false}
            field="broker_slug"
            dataCy="assigned-list"
            enabledHeaderColor={true}
            data={assignedBrokers || []}
            columns={assignedBrokersColumns}
            onRowClicked={onAssignedRowClicked}
            title={i18n.t('brokers.active_brokers')}
            disableSelectable={selectedAvailableRow ? true : false}
          />
        </Box>
      </Grid>
    </Grid>
  );
};

export default BrokersConnection;
